import { Component } from '@builder.io/sdk';
import { linkRegexConfig } from '@/components/BuilderIO/utils/commonFields';
import { TitleFields, MarginField, AnchorField, OutlineMarginField, ActionsField, MaxWidthField } from './CommonFields';

const commonComponents: { tabName: string; path: string; components: Record<string, Component> } = {
  tabName: 'Common components',
  path: 'common',
  components: {
    Breadcrumbs: {
      name: 'BreadCrumbs',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F83aa0d19c1df4f7baeccd61b180ae2aa',
      inputs: [
        {
          name: 'currentPageName',
          type: 'text',
          required: true,
        },
        {
          name: 'currentUri',
          type: 'url',
          regex: { ...linkRegexConfig },
          required: true,
        },
        MarginField,
        AnchorField,
        {
          name: 'crumbs',
          type: 'list',
          subFields: [
            {
              name: 'pageName',
              type: 'text',
              required: true,
            },
            {
              name: 'uri',
              type: 'url',
              regex: { ...linkRegexConfig },
              defaultValue: '/',
            },
          ],
        },
      ],
    },
    SlimlineBanner: {
      name: 'SlimlineBanner',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F8734b622ec214e869240b62c33e71081',
      inputs: [
        {
          name: 'desktopText',
          type: 'richText',
          required: true,
        },
        {
          name: 'mobileText',
          type: 'richText',
          required: true,
        },
        {
          name: 'link',
          type: 'url',
          regex: { ...linkRegexConfig },
          required: false,
        },
        {
          name: 'iconRight',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: false,
        },
        {
          name: 'iconRightAlt',
          type: 'text',
          required: false,
        },
        {
          name: 'iconLeft',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: false,
        },
        {
          name: 'iconLeftAlt',
          type: 'text',
          required: false,
        },
        {
          name: 'backgroundHexColor',
          type: 'color',
          required: false,
        },
        {
          name: 'textHexColor',
          type: 'color',
          required: false,
        },
        {
          name: 'gaEvents',
          type: 'object',
          subFields: [
            {
              name: 'dataAttributeName',
              type: 'text',
              required: false,
            },
            {
              name: 'dataAttributeValue',
              type: 'text',
              required: false,
            },
          ],
        },
        AnchorField,
      ],
    },
    TwoLineSlimLineBanner: {
      name: 'TwoLineSlimLineBanner',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F8734b622ec214e869240b62c33e71081',
      hideFromInsertMenu: true,
      inputs: [
        {
          name: 'desktopText',
          type: 'richText',
          required: true,
          helperText: 'Exactly two lines of text must be entered.',
        },
        {
          name: 'mobileText',
          type: 'richText',
          required: true,
          helperText: 'Exactly two lines of text must be entered.',
        },
        {
          name: 'link',
          type: 'url',
          regex: { ...linkRegexConfig },
          required: false,
        },
        {
          name: 'iconRight',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: false,
        },
        {
          name: 'iconRightAlt',
          type: 'text',
          required: false,
        },
        {
          name: 'iconLeft',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: false,
        },
        {
          name: 'iconLeftAlt',
          type: 'text',
          required: false,
        },
        {
          name: 'backgroundHexColor',
          type: 'color',
          required: false,
        },
        {
          name: 'textHexColor',
          type: 'color',
          required: false,
        },
        {
          name: 'gaEvents',
          type: 'object',
          subFields: [
            {
              name: 'dataAttributeName',
              type: 'text',
              required: false,
            },
            {
              name: 'dataAttributeValue',
              type: 'text',
              required: false,
            },
          ],
        },
        AnchorField,
      ],
    },
    DropdownSlimlineBanner: {
      name: 'DropdownSlimlineBanner',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F8734b622ec214e869240b62c33e71081',
      inputs: [
        {
          name: 'desktopText',
          friendlyName: 'Desktop Banner Text',
          type: 'richText',
          required: true,
        },
        {
          name: 'mobileText',
          friendlyName: 'Mobile Banner Text',
          type: 'richText',
          required: true,
        },
        {
          name: 'iconLeft',
          friendlyName: 'Banner Icon Left',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: false,
        },
        {
          name: 'iconLeftAlt',
          friendlyName: 'Banner Icon Left Alt',
          type: 'text',
          required: false,
        },
        {
          name: 'backgroundHexColor',
          friendlyName: 'Banner Background Color',
          type: 'color',
          required: false,
        },
        {
          name: 'textHexColor',
          friendlyName: 'Banner Text Color',
          type: 'color',
          required: false,
        },
        {
          name: 'listItems',
          type: 'list',
          friendlyName: 'Left List Items',
          helperText: 'Add max 3 items',
          onChange: options => {
            if (options.get('listItems').length > 3) {
              options.set('listItems', options.get('listItems').slice(0, 3));
            }
          },
          subFields: [
            {
              name: 'itemLabel',
              friendlyName: 'Item Title',
              type: 'text',
              required: true,
            },
            {
              name: 'url',
              friendlyName: 'Item Link',
              type: 'url',
              regex: { ...linkRegexConfig },
              required: true,
            },
            {
              name: 'helpLink',
              friendlyName: 'Help Text',
              type: 'object',
              subFields: [
                {
                  name: 'label',
                  friendlyName: 'Help Text',
                  type: 'text',
                },
                {
                  name: 'url',
                  friendlyName: 'Help Text Link',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
              ],
            },
          ],
        },
        {
          name: 'gridTitle',
          type: 'object',
          friendlyName: 'Right Grid Title',
          subFields: [
            {
              name: 'gridLabel',
              friendlyName: 'Grid Title',
              type: 'text',
              required: true,
            },
            {
              name: 'gridUrl',
              friendlyName: 'Grid Link',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
          ],
        },
        {
          name: 'gridListItems',
          friendlyName: 'Right Grid List Items',
          type: 'list',
          helperText: 'Add max 10 items',
          onChange: options => {
            if (options.get('gridListItems').length > 10) {
              options.set('gridListItems', options.get('gridListItems').slice(0, 10));
            }
          },
          subFields: [
            {
              name: 'itemLabel',
              friendlyName: 'Item Title',
              type: 'text',
              required: true,
            },
            {
              name: 'url',
              friendlyName: 'Item URL',
              type: 'url',
              regex: { ...linkRegexConfig },
              required: true,
            },
            {
              name: 'icon',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
            },
            {
              name: 'alt',
              type: 'text',
              helperText: 'Alternative text',
            },
          ],
        },
        {
          name: 'gaEvents',
          type: 'object',
          subFields: [
            {
              name: 'dataAttributeName',
              type: 'text',
              required: false,
            },
            {
              name: 'dataAttributeValue',
              type: 'text',
              required: false,
            },
          ],
        },
        AnchorField,
      ],
    },
    SlimHeroBanner: {
      name: 'SlimHeroBanner',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F64dc7026f1c94142b45dccbe5e1afe17',
      inputs: [
        {
          name: 'background',
          helperText: 'In case of a video background, the images are used as Video thumbnails',
          type: 'object',
          subFields: [
            {
              name: 'desktop',
              type: 'file',
              defaultValue: '/bee2-ui/img/homepage/BE_HP_HeroBanner_Desktop.jpg',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'mobile',
              type: 'file',
              defaultValue: '/bee2-ui/img/homepage/BE_HP_HeroBanner_Mobile.jpg',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
          ],
        },
        {
          name: 'imgAlt',
          type: 'string',
          defaultValue: 'Set the Alt value for the image here.',
          required: true,
        },
        { name: 'title', type: 'text' },
        { name: 'description', type: 'richText' },
        {
          name: 'actions',
          type: 'list',
          onChange: options => {
            if (options.get['actions'].length > 2) {
              options.set('actions', options.get('actions').slice(0, 2));
            }
          },
          subFields: [
            {
              name: 'isDropdownButton',
              type: 'boolean',
              defaultValue: false,
              friendlyName: 'Is this a dropdown button?',
              helperText:
                'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
            },
            {
              name: 'label',
              type: 'text',
            },
            {
              name: 'variant',
              friendlyName: 'Desktop variant',
              type: 'text',
              enum: ['primary', 'secondary'],
              defaultValue: 'primary',
            },
            {
              name: 'color',
              friendlyName: 'Desktop color',
              type: 'text',
              enum: ['light', 'dark'],
              defaultValue: 'dark',
            },
            {
              name: 'mobileVariant',
              type: 'text',
              enum: ['primary', 'secondary'],
              defaultValue: 'primary',
            },
            {
              name: 'mobileColor',
              type: 'text',
              enum: ['light', 'dark'],
              defaultValue: 'dark',
            },
            {
              name: 'href',
              type: 'url',
              regex: { ...linkRegexConfig },
              showIf: `!options.get('isDropdownButton')`,
            },
            {
              name: 'actions',
              type: 'list',
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
              ],
              showIf: `options.get('isDropdownButton')`,
            },
          ],
        },
        {
          name: 'textColor',
          friendlyName: 'Desktop Copy Color',
          type: 'string',
          enum: ['white', 'black'],
          defaultValue: 'white',
        },
        {
          name: 'mobileTextColor',
          friendlyName: 'Mobile Copy Color',
          type: 'string',
          enum: ['white', 'black'],
          defaultValue: 'white',
        },
        {
          name: 'whiteTextBackground',
          type: 'boolean',
          defaultValue: false,
        },
        {
          name: 'desktopBoxCopyPosition',
          type: 'text',
          defaultValue: 'right',
          enum: ['left', 'center', 'right'],
        },
        {
          name: 'desktopCopyPosition',
          type: 'text',
          defaultValue: 'left',
          enum: ['left', 'center'],
        },
        MarginField,
        AnchorField,
      ],
    },
    StandardHeroBanner: {
      name: 'StandardHeroBanner',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F64dc7026f1c94142b45dccbe5e1afe17',
      inputs: [
        {
          name: 'background',
          helperText: 'In case of a video background, the images are used as Video thumbnails',
          type: 'object',
          subFields: [
            {
              name: 'desktop',
              type: 'file',
              defaultValue: '/bee2-ui/img/homepage/BE_HP_HeroBanner_Desktop.jpg',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'mobile',
              type: 'file',
              defaultValue: '/bee2-ui/img/homepage/BE_HP_HeroBanner_Mobile.jpg',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
          ],
        },
        {
          name: 'altImageText',
          type: 'string',
          defaultValue: 'Set the Alt value for the image here.',
          required: true,
        },
        {
          name: 'isWistia',
          type: 'boolean',
          defaultValue: false,
          friendlyName: 'Wistia video link?',
        },
        {
          name: 'desktopVideoUri',
          friendlyName: 'Desktop Wistia Video URL',
          type: 'url',
          required: false,
          showIf: `options.get('isWistia')`,
        },
        {
          name: 'mobileVideoUri',
          friendlyName: 'Mobile Wistia Video URL ',
          type: 'url',
          required: false,
          showIf: `options.get('isWistia')`,
        },
        {
          name: 'fileVideoSrc',
          friendlyName: 'Video Source',
          type: 'file',
          allowedFileTypes: ['mp4', 'mov'],
          required: false,
          showIf: `!options.get('isWistia')`,
        },
        {
          name: 'fileMobileVideoSrc',
          friendlyName: 'Mobile Video Source',
          type: 'file',
          allowedFileTypes: ['mp4', 'mov'],
          required: false,
          showIf: `!options.get('isWistia')`,
        },
        {
          name: 'autoPlay',
          type: 'boolean',
          defaultValue: true,
          showIf: `!options.get('isWistia')`,
        },
        {
          name: 'loop',
          type: 'boolean',
          defaultValue: true,
          showIf: `!options.get('isWistia')`,
        },
        {
          name: 'controls',
          type: 'boolean',
          defaultValue: true,
          showIf: `!options.get('isWistia')`,
        },
        {
          name: 'hasSound',
          type: 'boolean',
          defaultValue: true,
        },
        { name: 'title', friendlyName: 'Desktop Title', type: 'text' },
        { name: 'mobileTitle', friendlyName: 'Mobile Title', type: 'text' },
        { name: 'description', type: 'richText' },
        {
          name: 'actions',
          type: 'list',
          onChange: options => {
            if (options.get['actions'].length > 2) {
              options.set('actions', options.get('actions').slice(0, 2));
            }
          },
          subFields: [
            {
              name: 'isDropdownButton',
              type: 'boolean',
              defaultValue: false,
              friendlyName: 'Is this a dropdown button?',
              helperText:
                'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
            },
            {
              name: 'label',
              type: 'text',
            },
            {
              name: 'variant',
              friendlyName: 'Desktop variant',
              type: 'text',
              enum: ['primary', 'secondary'],
              defaultValue: 'primary',
            },
            {
              name: 'color',
              friendlyName: 'Desktop color',
              type: 'text',
              enum: ['light', 'dark'],
              defaultValue: 'dark',
            },
            {
              name: 'mobileVariant',
              type: 'text',
              enum: ['primary', 'secondary'],
              defaultValue: 'primary',
            },
            {
              name: 'mobileColor',
              type: 'text',
              enum: ['light', 'dark'],
              defaultValue: 'dark',
            },
            {
              name: 'href',
              type: 'url',
              regex: { ...linkRegexConfig },
              showIf: `!options.get('isDropdownButton')`,
            },
            {
              name: 'actions',
              type: 'list',
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
              ],
              showIf: `options.get('isDropdownButton')`,
            },
          ],
        },
        {
          name: 'copyColor',
          friendlyName: 'Desktop Copy Color',
          type: 'string',
          defaultValue: 'black',
          enum: ['white', 'black'],
          required: false,
        },
        {
          name: 'sectionLink',
          type: 'url',
          regex: { ...linkRegexConfig },
          required: false,
        },
        {
          name: 'desktopCopyPosition',
          type: 'text',
          defaultValue: 'right',
          enum: ['left', 'right', 'center', 'center/center'],
        },
        MarginField,
        AnchorField,
      ],
    },
    PromoHeroBanner: {
      name: 'PromoHeroBanner',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F64dc7026f1c94142b45dccbe5e1afe17',
      inputs: [MarginField, AnchorField],
    },
    LargeHeroBanner: {
      name: 'LargeHeroBanner',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F64dc7026f1c94142b45dccbe5e1afe17',
      inputs: [
        {
          name: 'background',
          helperText: 'In case of a video background, the images are used as Video thumbnails',
          type: 'object',
          subFields: [
            {
              name: 'desktop',
              type: 'file',
              defaultValue: '/bee2-ui/img/homepage/BE_HP_HeroBanner_Desktop.jpg',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'mobile',
              type: 'file',
              defaultValue: '/bee2-ui/img/homepage/BE_HP_HeroBanner_Mobile.jpg',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
          ],
        },
        {
          name: 'altImageText',
          type: 'string',
          defaultValue: 'Set the Alt value for the image here.',
          required: true,
        },
        {
          name: 'isWistia',
          type: 'boolean',
          defaultValue: false,
          friendlyName: 'Wistia video link?',
        },
        {
          name: 'desktopVideoUri',
          friendlyName: 'Desktop Wistia Video URL',
          type: 'url',
          required: false,
          showIf: `options.get('isWistia')`,
        },
        {
          name: 'mobileVideoUri',
          friendlyName: 'Mobile Wistia Video URL ',
          type: 'url',
          required: false,
          showIf: `options.get('isWistia')`,
        },
        {
          name: 'fileVideoSrc',
          friendlyName: 'Video Source',
          type: 'file',
          allowedFileTypes: ['mp4', 'mov'],
          required: false,
          showIf: `!options.get('isWistia')`,
        },
        {
          name: 'fileMobileVideoSrc',
          friendlyName: 'Mobile Video Source',
          type: 'file',
          allowedFileTypes: ['mp4', 'mov'],
          required: false,
          showIf: `!options.get('isWistia')`,
        },
        {
          name: 'autoPlay',
          type: 'boolean',
          defaultValue: true,
          showIf: `!options.get('isWistia')`,
        },
        {
          name: 'loop',
          type: 'boolean',
          defaultValue: true,
          showIf: `!options.get('isWistia')`,
        },
        {
          name: 'controls',
          type: 'boolean',
          defaultValue: true,
          showIf: `!options.get('isWistia')`,
        },
        {
          name: 'hasSound',
          type: 'boolean',
          defaultValue: true,
        },
        { name: 'title', friendlyName: 'Desktop Title', type: 'text' },
        { name: 'mobileTitle', friendlyName: 'Mobile Title', type: 'text' },
        { name: 'description', type: 'richText' },
        {
          name: 'actions',
          type: 'list',
          onChange: options => {
            if (options.get['actions'].length > 2) {
              options.set('actions', options.get('actions').slice(0, 2));
            }
          },
          subFields: [
            {
              name: 'isDropdownButton',
              type: 'boolean',
              defaultValue: false,
              friendlyName: 'Is this a dropdown button?',
              helperText:
                'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
            },
            {
              name: 'label',
              type: 'text',
            },
            {
              name: 'variant',
              friendlyName: 'Desktop variant',
              type: 'text',
              enum: ['primary', 'secondary'],
              defaultValue: 'primary',
            },
            {
              name: 'color',
              friendlyName: 'Desktop color',
              type: 'text',
              enum: ['light', 'dark'],
              defaultValue: 'dark',
            },
            {
              name: 'mobileVariant',
              type: 'text',
              enum: ['primary', 'secondary'],
              defaultValue: 'primary',
            },
            {
              name: 'mobileColor',
              type: 'text',
              enum: ['light', 'dark'],
              defaultValue: 'dark',
            },
            {
              name: 'href',
              type: 'url',
              regex: { ...linkRegexConfig },
              showIf: `!options.get('isDropdownButton')`,
            },
            {
              name: 'actions',
              type: 'list',
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  type: 'text',
                },
              ],
              showIf: `options.get('isDropdownButton')`,
            },
          ],
        },
        {
          name: 'copyColor',
          type: 'string',
          defaultValue: 'black',
          enum: ['white', 'black'],
          required: false,
        },
        {
          name: 'mobileCopyColor',
          type: 'string',
          defaultValue: 'black',
          enum: ['white', 'black'],
          required: false,
        },
        {
          name: 'sectionLink',
          type: 'url',
          regex: { ...linkRegexConfig },
          required: false,
        },
        {
          name: 'desktopCopyPosition',
          type: 'text',
          defaultValue: 'right',
          enum: ['left', 'right', 'center', 'center/center'],
        },
        MarginField,
        AnchorField,
      ],
    },
    DoubleImageTile: {
      name: 'DoubleImageTile',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F38a46dcb35354572b0a81253bbfcab97',
      inputs: [
        {
          name: 'tiles',
          type: 'list',
          subFields: [
            {
              name: 'isVideo',
              friendlyName: 'Video',
              type: 'boolean',
              defaultValue: false,
            },
            {
              name: 'videoThumbnail',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png'],
              showIf: `options.get('isVideo')`,
            },
            {
              name: 'imgSrc',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'gif'],
              required: true,
              showIf: `!options.get('isVideo')`,
            },
            {
              name: 'imgAlt',
              type: 'text',
              required: true,
              showIf: `!options.get('isVideo')`,
            },
            {
              name: 'imgLink',
              type: 'text',
              showIf: `!options.get('isVideo')`,
            },
            {
              name: 'videoSrc',
              type: 'file',
              allowedFileTypes: ['mp4', 'mov'],
              showIf: `options.get('isVideo')`,
            },
            {
              name: 'autoPlay',
              type: 'boolean',
              defaultValue: true,
              showIf: `options.get('isVideo')`,
            },
            {
              name: 'loop',
              type: 'boolean',
              defaultValue: true,
              showIf: `options.get('isVideo')`,
            },
            {
              name: 'controls',
              type: 'boolean',
              defaultValue: true,
              showIf: `options.get('isVideo')`,
            },
            {
              name: 'optionalHeader',
              friendlyName: 'Item Title',
              type: 'text',
            },
            {
              name: 'optionalSubHeader',
              friendlyName: 'Item Description',
              type: 'richText',
            },
            {
              name: 'ctas',
              friendlyName: 'Actions',
              type: 'list',
              subFields: [
                {
                  name: 'isDropdownButton',
                  type: 'boolean',
                  defaultValue: false,
                  friendlyName: 'Is this a dropdown button?',
                  helperText:
                    'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
                },
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  friendlyName: 'Link',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                  showIf: `!options.get('isDropdownButton')`,
                },
                {
                  name: 'variant',
                  type: 'string',
                  defaultValue: 'secondary',
                  enum: ['primary', 'secondary'],
                  required: false,
                },
                {
                  name: 'color',
                  type: 'string',
                  defaultValue: 'dark',
                  enum: ['light', 'dark'],
                  required: false,
                },
                {
                  name: 'actions',
                  type: 'list',
                  showIf: `options.get('isDropdownButton')`,
                  subFields: [
                    {
                      name: 'label',
                      type: 'text',
                    },
                    {
                      name: 'href',
                      type: 'url',
                      regex: { ...linkRegexConfig },
                    },
                    {
                      name: 'type',
                      type: 'string',
                      defaultValue: 'secondary',
                      enum: ['primary', 'secondary'],
                      required: false,
                    },
                    {
                      name: 'color',
                      type: 'string',
                      defaultValue: 'dark',
                      enum: ['light', 'dark'],
                      required: false,
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          name: 'textBoxPosition',
          defaultValue: 'left',
          enum: ['left', 'center'],
          type: 'string',
        },
        MaxWidthField,
        MarginField,
        AnchorField,
      ],
    },
    SingleImageTile: {
      name: 'SingleImageTile',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F64dc7026f1c94142b45dccbe5e1afe17',
      inputs: [
        {
          name: 'image',
          type: 'file',
          friendlyName: 'Desktop Image',
          helperText: 'In case of a video background, the images are used as Video thumbnails',
          defaultValue: '/bee2-ui/img/homepage/Capture-Collection-Desktop.jpg',
          allowedFileTypes: ['jpeg', 'jpg', 'gif'],
          required: false,
        },
        {
          name: 'mobileImage',
          type: 'file',
          helperText: 'In case of a video background, the images are used as Video thumbnails',
          defaultValue: '/bee2-ui/img/homepage/Capture-Collection-Mobile.png',
          allowedFileTypes: ['jpeg', 'jpg', 'gif'],
          required: false,
        },
        {
          name: 'altImageText',
          type: 'string',
          defaultValue: 'Set the Alt value for the image here.',
          required: true,
        },
        {
          name: 'imageLink',
          type: 'url',
          regex: { ...linkRegexConfig },
          required: false,
        },
        {
          name: 'textOnImage',
          type: 'object',
          subFields: [
            {
              name: 'textOnImageText',
              helperText: 'Option Heading on image',
              type: 'string',
            },
            {
              name: 'textOnImageStyles',
              helperText: 'default value is h1',
              type: 'string',
              defaultValue: 'h1',
              enum: ['h1', 'h2'],
            },
            {
              name: 'textOnImageTag',
              helperText: 'default value is h2',
              type: 'string',
              defaultValue: 'h2',
              enum: ['h1', 'h2'],
            },
          ],
        },

        {
          name: 'wistiaVideo',
          friendlyName: 'Wistia Video',
          type: 'boolean',
          defaultValue: false,
        },
        {
          name: 'desktopVideoSource',
          type: 'file',
          allowedFileTypes: ['mp4', 'mov'],
          showIf: `!options.get('wistiaVideo')`,
        },
        {
          name: 'mobileVideoSource',
          type: 'file',
          allowedFileTypes: ['mp4', 'mov'],
          showIf: `!options.get('wistiaVideo')`,
        },
        {
          name: 'desktopVideoUri',
          friendlyName: 'Desktop Wistia Video URL',
          type: 'url',
          required: false,
          showIf: `options.get('wistiaVideo')`,
        },
        {
          name: 'mobileVideoUri',
          friendlyName: 'Mobile Wistia Video URL',
          type: 'url',
          required: false,
          showIf: `options.get('wistiaVideo')`,
        },
        {
          name: 'videoTitle',
          friendlyName: 'Video title',
          helperText: 'Video title description for screen readers',
          type: 'string',
          required: false,
          showIf: `options.get('wistiaVideo')`,
        },
        {
          name: 'controls',
          type: 'boolean',
          defaultValue: false,
          showIf: `!options.get('wistiaVideo')`,
        },
        {
          name: 'loop',
          type: 'boolean',
          defaultValue: false,
          showIf: `!options.get('wistiaVideo')`,
        },
        {
          name: 'autoplay',
          type: 'boolean',
          defaultValue: false,
          showIf: `!options.get('wistiaVideo')`,
        },
        {
          name: 'items',
          type: 'array',
          subFields: [
            {
              name: 'heading',
              type: 'string',
              defaultValue: 'Large Header',
            },
            {
              name: 'headingTag',
              friendlyName: 'default value is h3',
              type: 'string',
              defaultValue: 'h3',
              enum: ['h2', 'h3'],
            },
            {
              name: 'paragraph',
              friendlyName: 'Optional subCopy',
              type: 'richText',
              defaultValue:
                'Example text here this line is optional. Paragraph style. Example text here this line is optional.',
            },
            {
              name: 'actions',
              friendlyName: 'option Actions',
              type: 'list',
              subFields: [
                {
                  name: 'dropdown',
                  type: 'boolean',
                  defaultValue: false,
                  friendlyName: 'Is this a dropdown button?',
                  helperText:
                    'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
                },
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  friendlyName: 'Link',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                  showIf: `!options.get('dropdown')`,
                },
                {
                  name: 'type',
                  type: 'string',
                  defaultValue: 'secondary',
                  enum: ['link', 'primary', 'secondary'],
                  required: false,
                },
                {
                  name: 'color',
                  type: 'string',
                  defaultValue: 'dark',
                  enum: ['light', 'dark'],
                  required: false,
                },
                {
                  name: 'actions',
                  type: 'list',
                  showIf: `options.get('dropdown')`,
                  subFields: [
                    {
                      name: 'label',
                      type: 'text',
                    },
                    {
                      name: 'href',
                      type: 'url',
                      regex: { ...linkRegexConfig },
                    },
                    {
                      name: 'type',
                      type: 'string',
                      defaultValue: 'secondary',
                      enum: ['primary', 'secondary'],
                      required: false,
                    },
                    {
                      name: 'color',
                      type: 'string',
                      defaultValue: 'dark',
                      enum: ['light', 'dark'],
                      required: false,
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          name: 'isTextCenter',
          helperText: 'center the header, sub-copy, and CTA',
          type: 'boolean',
          defaultValue: false,
        },
        MaxWidthField,
        MarginField,
        AnchorField,
      ],
    },
    StorytellingTiles: {
      name: 'StorytellingTiles',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fa4cf8657d58a4d04bca3e23822355fcc',
      inputs: [
        {
          name: 'header',
          type: 'text',
          defaultValue: 'Trend Alert: Distinctive Designs',
        },
        {
          name: 'subHeader',
          type: 'text',
          defaultValue: 'Say I do with the most sought-after bridal styles.',
        },
        {
          name: 'tiles',
          type: 'list',
          defaultValue: [
            {
              imgSrc: '/bee2-ui/img/homepage/HP-Layers.jpg',
              imgAlt: '',
              optionalHeader: 'Layer by Layer',
              optionalLinkLabel: 'Shop Stacking Rings',
              optionalLinkUrl: '/design-your-own-ring-stack/',
            },
            {
              imgSrc: '/bee2-ui/img/homepage/HP-Stacked.jpg',
              imgAlt: '',
              optionalHeader: 'Endless Sparkle',
              optionalLinkLabel: 'Shop Eternity Rings',
              optionalLinkUrl: '/rings/diamond-eternity-bands-women/',
            },
            {
              imgSrc: '/bee2-ui/img/homepage/HP-Sparkle.jpg',
              imgAlt: '',
              optionalHeader: 'Perfect Pairs',
              optionalLinkLabel: 'Shop Bridal Sets',
              optionalLinkUrl: '/wedding-ring-sets/',
            },
            {
              imgSrc: '/bee2-ui/img/homepage/HP- Mens.jpg',
              imgAlt: '',
              optionalHeader: 'For the Groom',
              optionalLinkLabel: 'Shop Trends for Men',
              optionalLinkUrl: '/search/?tag=mens-wedding-ring-trends',
            },
          ],
          subFields: [
            {
              name: 'isVideo',
              friendlyName: 'Video',
              type: 'boolean',
              defaultValue: false,
            },
            {
              name: 'videoThumbnail',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png'],
              showIf: `options.get('isVideo')`,
            },
            {
              name: 'videoSrc',
              type: 'file',
              allowedFileTypes: ['mp4', 'mov'],
              showIf: `options.get('isVideo')`,
            },
            {
              name: 'autoPlay',
              type: 'boolean',
              defaultValue: true,
              showIf: `options.get('isVideo')`,
            },
            {
              name: 'loop',
              type: 'boolean',
              defaultValue: true,
              showIf: `options.get('isVideo')`,
            },
            {
              name: 'controls',
              type: 'boolean',
              defaultValue: true,
              showIf: `options.get('isVideo')`,
            },
            {
              name: 'imgSrc',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'gif'],
              showIf: `!options.get('isVideo')`,
            },
            {
              name: 'imgAlt',
              type: 'text',
              showIf: `!options.get('isVideo')`,
            },
            {
              name: 'optionalHeader',
              type: 'text',
            },
            {
              name: 'optionalLinkLabel',
              type: 'text',
            },
            {
              name: 'optionalLinkUrl',
              regex: { ...linkRegexConfig },
              type: 'url',
            },
          ],
        },
        AnchorField,
      ],
    },
    GatewayCopyBlock: {
      name: 'GatewayCopyBlock',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F78228096521e403f896fadb423c5db43',
      inputs: [
        {
          name: 'background',
          type: 'text',
          defaultValue: 'White',
          enum: ['Dark Green', 'Light Green', 'Light Mauve', 'Cream', 'White'],
        },
        {
          name: 'isOneThird',
          helperText: 'if true, the image is 2/3 of the component width, false is 1/2',
          type: 'boolean',
          defaultValue: false,
        },
        {
          name: 'optionalEyebrowHeading',
          type: 'text',
        },
        {
          name: 'optionalLargeHeader',
          type: 'text',
        },
        {
          name: 'description',
          type: 'richText',
        },
        {
          name: 'alignment',
          type: 'string',
          defaultValue: 'left',
          enum: ['left', 'center'],
        },
        {
          name: 'mediaLeft',
          type: 'boolean',
          defaultValue: false,
        },
        {
          name: 'isVideo',
          friendlyName: 'Video',
          type: 'boolean',
          defaultValue: false,
        },
        {
          name: 'videoSrc',
          type: 'file',
          allowedFileTypes: ['mp4', 'mov'],
          showIf: `options.get('isVideo')`,
        },
        {
          name: 'videoThumbnail',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png'],
          showIf: `options.get('isVideo')`,
        },
        {
          name: 'autoPlay',
          type: 'boolean',
          defaultValue: true,
          showIf: `options.get('isVideo')`,
        },
        {
          name: 'loop',
          type: 'boolean',
          defaultValue: true,
          showIf: `options.get('isVideo')`,
        },
        {
          name: 'controls',
          type: 'boolean',
          defaultValue: true,
          showIf: `options.get('isVideo')`,
        },
        {
          name: 'imgSrc',
          friendlyName: 'DesktopImage',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          showIf: `!options.get('isVideo')`,
        },
        {
          name: 'MobileImageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          showIf: `!options.get('isVideo')`,
        },
        {
          name: 'imgAlt',
          type: 'text',
          showIf: `!options.get('isVideo')`,
        },
        {
          name: 'imgLink',
          type: 'text',
        },
        {
          name: 'imgGaEvents',
          friendlyName: 'Img GA Events',
          type: 'list',
          subFields: [
            {
              name: 'dataAttributes',
              type: 'list',
              subFields: [
                {
                  name: 'dataAttributeName',
                  type: 'text',
                },
                {
                  name: 'dataAttributeValue',
                  type: 'text',
                },
              ],
            },
          ],
        },
        {
          name: 'actions',
          friendlyName: 'Actions',
          type: 'list',
          subFields: [
            {
              name: 'dropdown',
              type: 'boolean',
              defaultValue: false,
              friendlyName: 'Is this a dropdown button?',
              helperText:
                'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
            },
            {
              name: 'label',
              required: true,
              type: 'text',
            },
            {
              name: 'href',
              friendlyName: 'Link',
              type: 'url',
              regex: { ...linkRegexConfig },
              required: true,
              showIf: `!options.get('dropdown')`,
            },
            {
              name: 'type',
              type: 'string',
              defaultValue: 'secondary',
              enum: ['primary', 'secondary'],
              required: false,
            },
            {
              name: 'color',
              type: 'string',
              defaultValue: 'dark',
              enum: ['light', 'dark'],
              required: false,
            },
            {
              name: 'gaEvents',
              type: 'list',
              subFields: [
                {
                  name: 'dataAttributes',
                  type: 'list',
                  subFields: [
                    {
                      name: 'dataAttributeName',
                      type: 'text',
                    },
                    {
                      name: 'dataAttributeValue',
                      type: 'text',
                    },
                  ],
                },
              ],
            },
            {
              name: 'actions',
              type: 'list',
              showIf: `options.get('dropdown')`,
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
                {
                  name: 'type',
                  type: 'string',
                  defaultValue: 'secondary',
                  enum: ['primary', 'secondary'],
                  required: false,
                },
                {
                  name: 'color',
                  type: 'string',
                  defaultValue: 'dark',
                  enum: ['light', 'dark'],
                  required: false,
                },
                {
                  name: 'gaEvents',
                  type: 'list',
                  subFields: [
                    {
                      name: 'dataAttributes',
                      type: 'list',
                      subFields: [
                        {
                          name: 'dataAttributeName',
                          type: 'text',
                        },
                        {
                          name: 'dataAttributeValue',
                          type: 'text',
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          name: 'isFullBleed',
          type: 'boolean',
          defaultValue: false,
          helperText: 'Turn on to remove margin to the component',
        },
        MaxWidthField,
        {
          ...MarginField,
          friendlyName: 'Desktop Margin',
        },
        {
          name: 'mobileMargin',
          type: 'boolean',
          defaultValue: false,
          helperText: 'Turn on to add margin to the bottom of the component',
        },
        AnchorField,
      ],
    },
    ImageLinkTile: {
      name: 'ImageLinkTile',
      inputs: [
        {
          name: 'backgroundImage',
          type: 'file',
          allowedFileTypes: ['jpg', 'jpeg', 'svg', 'png'],
        },
        {
          name: 'mobileBackgroundImage',
          type: 'file',
          allowedFileTypes: ['jpg', 'jpeg', 'svg', 'png'],
        },
        {
          name: 'sectionLink',
          type: 'url',
          regex: { ...linkRegexConfig },
          required: false,
        },
        {
          name: 'sectionHeader',
          type: 'object',
          subFields: [
            {
              name: 'optionalLargeHeader',
              type: 'object',
              subFields: [
                {
                  name: 'content',
                  type: 'text',
                },
                {
                  name: 'titleHTMLTag',
                  type: 'string',
                  defaultValue: 'h2',
                  helperText: 'Defaults to h2 tag',
                  enum: ['h1', 'h2'],
                },
              ],
            },
            { name: 'optionalDescription', type: 'richText' },
          ],
        },
        {
          name: 'colorText',
          type: 'text',
          defaultValue: 'black',
          enum: ['white', 'black'],
        },
        {
          name: 'ctas',
          friendlyName: 'Actions',
          type: 'list',
          subFields: [
            {
              name: 'isGeneralLinkTileCtaDropdown',
              type: 'boolean',
              defaultValue: false,
              friendlyName: 'Is this a dropdown button?',
              helperText:
                'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
            },
            {
              name: 'label',
              type: 'text',
            },
            {
              name: 'href',
              friendlyName: 'Link',
              type: 'url',
              regex: { ...linkRegexConfig },
              showIf: `!options.get('isGeneralLinkTileCtaDropdown')`,
            },
            {
              name: 'type',
              friendlyName: 'Variant',
              type: 'string',
              defaultValue: 'secondary',
              enum: ['primary', 'secondary'],
              required: false,
            },
            {
              name: 'color',
              type: 'string',
              defaultValue: 'dark',
              enum: ['light', 'dark'],
              required: false,
            },
            {
              name: 'actions',
              type: 'list',
              showIf: `options.get('isGeneralLinkTileCtaDropdown')`,
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
                {
                  name: 'type',
                  friendlyName: 'Variant',
                  type: 'string',
                  defaultValue: 'secondary',
                  enum: ['primary', 'secondary'],
                  required: false,
                },
                {
                  name: 'color',
                  type: 'string',
                  defaultValue: 'dark',
                  enum: ['light', 'dark'],
                  required: false,
                },
              ],
            },
          ],
        },
        MarginField,
        AnchorField,
      ],
    },
    LinkTile: {
      name: 'LinkTile',
      inputs: [
        {
          name: 'background',
          type: 'text',
          defaultValue: 'White',
          enum: ['Dark Green', 'Light Green', 'Lighter Green', 'Light Mauve', 'Cream', 'White'],
        },
        {
          name: 'sectionHeader',
          type: 'object',
          subFields: [
            {
              name: 'optionalLargeHeader',
              type: 'object',
              subFields: [
                {
                  name: 'content',
                  type: 'text',
                },
                {
                  name: 'titleHTMLTag',
                  type: 'string',
                  defaultValue: 'h2',
                  helperText: 'Defaults to h2 tag',
                  enum: ['h1', 'h2'],
                },
              ],
            },
            { name: 'optionalDescription', type: 'richText' },
          ],
        },
        {
          name: 'linkGroups',
          friendlyName: 'Items',
          type: 'list',
          subFields: [
            {
              name: 'icon',
              type: 'object',
              subFields: [
                {
                  name: 'iconUrl',
                  type: 'file',
                  allowedFileTypes: ['jpg', 'jpeg', 'svg', 'png'],
                },
              ],
            },
            {
              name: 'label',
              type: 'object',
              subFields: [
                {
                  name: 'labelTag',
                  friendlyName: 'HTML Tag',
                  type: 'string',
                  enum: ['h1', 'h2', 'h3'],
                  defaultValue: 'h3',
                },
                {
                  name: 'labelText',
                  friendlyName: 'Label',
                  type: 'text',
                },
              ],
            },
            {
              name: 'linkDescription',
              friendlyName: 'Description',
              type: 'richText',
            },
            {
              name: 'isLinkedGroup',
              friendlyName: 'Pop-up',
              helperText:
                'Switch this toggle on if clicking the icon, title, and description should open a pop-up (i.e. Live Person, T&C Pop-up, etc.).',
              type: 'boolean',
              defaultValue: false,
            },
            {
              name: 'link',
              friendlyName: 'Link',
              type: 'url',
              showIf: `!options.get('isLinkedGroup')`,
              regex: { ...linkRegexConfig },
            },
            {
              name: 'onClick',
              friendlyName: 'Pop-up',
              type: 'string',
              required: false,
              showIf: `options.get('isLinkedGroup')`,
              enum: ['openLivePerson', 'openPromoModal', 'openEmailUsModal'],
            },
            {
              name: 'gaEvents',
              type: 'list',
              subFields: [
                {
                  name: 'dataAttributes',
                  type: 'list',
                  subFields: [
                    {
                      name: 'dataAttributeName',
                      type: 'text',
                    },
                    {
                      name: 'dataAttributeValue',
                      type: 'text',
                    },
                  ],
                },
              ],
            },
            {
              name: 'cta',
              friendlyName: 'Action',
              type: 'object',
              subFields: [
                {
                  name: 'isLinkGroupCtaAPopup',
                  type: 'boolean',
                  defaultValue: false,
                  friendlyName: 'Pop-up',
                  helperText:
                    'Switch this toggle on if clicking the button should open a pop-up (i.e. Live Person, T&C Pop-up, etc.)',
                },
                {
                  name: 'isLinkGroupCtaDropdown',
                  type: 'boolean',
                  defaultValue: false,
                  friendlyName: 'Is this a dropdown button?',
                  showIf: '!options.get("isLinkGroupCtaAPopup")',
                  helperText:
                    'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
                },
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'onClick',
                  friendlyName: 'Pop-up',
                  type: 'string',
                  required: false,
                  showIf: `options.get('isLinkGroupCtaAPopup')`,
                  enum: ['openLivePerson', 'openPromoModal', 'openEmailUsModal'],
                },
                {
                  name: 'href',
                  friendlyName: 'Link',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                  showIf: `!options.get('isLinkGroupCtaDropdown') && !options.get("isLinkGroupCtaAPopup")`,
                },
                {
                  name: 'type',
                  friendlyName: 'Variant',
                  type: 'string',
                  defaultValue: 'secondary',
                  enum: ['primary', 'secondary'],
                  required: false,
                },
                {
                  name: 'color',
                  type: 'string',
                  defaultValue: 'dark',
                  enum: ['light', 'dark'],
                  required: false,
                },
                {
                  name: 'gaEvents',
                  type: 'list',
                  subFields: [
                    {
                      name: 'dataAttributes',
                      type: 'list',
                      subFields: [
                        {
                          name: 'dataAttributeName',
                          type: 'text',
                        },
                        {
                          name: 'dataAttributeValue',
                          type: 'text',
                        },
                      ],
                    },
                  ],
                },
                {
                  name: 'actions',
                  type: 'list',
                  showIf: `options.get('isLinkGroupCtaDropdown')`,
                  subFields: [
                    {
                      name: 'label',
                      type: 'text',
                    },
                    {
                      name: 'href',
                      type: 'url',
                      regex: { ...linkRegexConfig },
                    },
                    {
                      name: 'type',
                      type: 'string',
                      defaultValue: 'secondary',
                      enum: ['primary', 'secondary'],
                      required: false,
                    },
                    {
                      name: 'color',
                      type: 'string',
                      defaultValue: 'dark',
                      enum: ['light', 'dark'],
                      required: false,
                    },
                    {
                      name: 'gaEvents',
                      type: 'list',
                      subFields: [
                        {
                          name: 'dataAttributes',
                          type: 'list',
                          subFields: [
                            {
                              name: 'dataAttributeName',
                              type: 'text',
                            },
                            {
                              name: 'dataAttributeValue',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          name: 'ctas',
          friendlyName: 'Actions',
          type: 'list',
          subFields: [
            {
              name: 'isGeneralLinkTileCtaDropdown',
              type: 'boolean',
              defaultValue: false,
              friendlyName: 'Is this a dropdown button?',
              helperText:
                'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
            },
            {
              name: 'label',
              type: 'text',
            },
            {
              name: 'href',
              friendlyName: 'Link',
              type: 'url',
              regex: { ...linkRegexConfig },
              showIf: `!options.get('isGeneralLinkTileCtaDropdown')`,
            },
            {
              name: 'type',
              friendlyName: 'Variant',
              type: 'string',
              defaultValue: 'secondary',
              enum: ['primary', 'secondary'],
              required: false,
            },
            {
              name: 'color',
              type: 'string',
              defaultValue: 'dark',
              enum: ['light', 'dark'],
              required: false,
            },
            {
              name: 'gaEvents',
              type: 'list',
              subFields: [
                {
                  name: 'dataAttributes',
                  type: 'list',
                  subFields: [
                    {
                      name: 'dataAttributeName',
                      type: 'text',
                    },
                    {
                      name: 'dataAttributeValue',
                      type: 'text',
                    },
                  ],
                },
              ],
            },
            {
              name: 'actions',
              type: 'list',
              showIf: `options.get('isGeneralLinkTileCtaDropdown')`,
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
                {
                  name: 'type',
                  friendlyName: 'Variant',
                  type: 'string',
                  defaultValue: 'secondary',
                  enum: ['primary', 'secondary'],
                  required: false,
                },
                {
                  name: 'color',
                  type: 'string',
                  defaultValue: 'dark',
                  enum: ['light', 'dark'],
                  required: false,
                },
                {
                  name: 'gaEvents',
                  type: 'list',
                  subFields: [
                    {
                      name: 'dataAttributes',
                      type: 'list',
                      subFields: [
                        {
                          name: 'dataAttributeName',
                          type: 'text',
                        },
                        {
                          name: 'dataAttributeValue',
                          type: 'text',
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
        MarginField,
        AnchorField,
      ],
    },
    MissionTile: {
      name: 'MissionTile',
      inputs: [
        {
          name: 'backgroundColor',
          helperText: 'Ex. #ffffff',
          type: 'string',
        },
        {
          name: 'title',
          type: 'object',
          subFields: [
            {
              name: 'text',
              type: 'richText',
            },
            {
              name: 'textColor',
              helperText: 'Ex. #ffffff',
              type: 'string',
            },
          ],
        },
        {
          name: 'iconTextColor',
          helperText: 'Ex. #ffffff',
          type: 'string',
        },
        {
          name: 'icons',
          type: 'list',
          defaultValue: [
            {
              icon: '/bee2-ui/img/homepage/diamond.png',
              eyebrow: {
                label: 'Beyond Conflict Free',
                weight: '350',
                lineBreak: true,
              },
              subCopy: {
                label: 'Learn More',
                href: 'https://www.brilliantearth.com/conflict-free-diamonds/',
                lineBreak: true,
              },
            },
            {
              icon: '/bee2-ui/img/homepage/recycle.png',
              eyebrow: {
                label: 'Recycled Precious Metals',
                weight: '350',
                lineBreak: true,
              },
              subCopy: {
                label: 'Learn More',
                href: 'https://www.brilliantearth.com/recycled-gold-jewelry/',
                lineBreak: true,
              },
            },
            {
              icon: '/bee2-ui/img/homepage/hand-shake.png',
              eyebrow: {
                label: 'Committed to Giving Back',
                weight: '350',
                lineBreak: true,
              },
              subCopy: {
                label: 'Learn More',
                href: 'https://www.brilliantearth.com/giving-back/',
                lineBreak: true,
              },
            },
          ],
          subFields: [
            {
              name: 'icon',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
            },
            {
              name: 'eyebrow',
              type: 'object',
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'weight',
                  type: 'string',
                  defaultValue: '350',
                  enum: ['350', '800'],
                },
                {
                  name: 'lineBreak',
                  type: 'boolean',
                  defaultValue: true,
                },
              ],
            },
            {
              name: 'subCopy',
              type: 'object',
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  friendlyName: 'Link',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                  required: true,
                },
                {
                  name: 'lineBreak',
                  type: 'boolean',
                  defaultValue: true,
                },
                {
                  name: 'weight',
                  type: 'string',
                  defaultValue: '350',
                  enum: ['350', '800'],
                },
              ],
            },
          ],
        },

        {
          name: 'description',
          type: 'object',
          subFields: [
            {
              name: 'text',
              type: 'string',
            },
            {
              name: 'textColor',
              helperText: 'Ex. #ffffff',
              type: 'string',
            },
          ],
          defaultValue:
            "Since our founding in 2005, we've been setting new industry standards to build a brighter future in the communities where we operate.",
        },
        {
          name: 'ctaColor',
          type: 'string',
          helperText: 'Ex. #ffffff',
        },
        {
          name: 'ctas',
          type: 'list',
          defaultValue: [{ label: 'Our Story', href: '/about-brilliant-earth/' }],
          subFields: [
            {
              name: 'label',
              type: 'text',
            },
            {
              name: 'href',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
          ],
        },
        {
          name: 'isFullBleed',
          type: 'boolean',
          defaultValue: false,
          helperText: 'Turn on to remove margin to the left and right of the component',
        },
        MaxWidthField,
        AnchorField,
        MarginField,
      ],
    },
    GatewayPromoTile: {
      name: 'GatewayPromoTile',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fab16f132401146929e2f88328a6826c3',
      inputs: [
        {
          name: 'ctaLabel',
          type: 'text',
          defaultValue: 'See Terms',
        },
        {
          name: 'isFullBleed',
          type: 'boolean',
          defaultValue: false,
          helperText: 'Turn on to remove margin to the left and right of the component',
        },
        MaxWidthField,
        AnchorField,
        MarginField,
      ],
    },
    HomepageCarousel: {
      name: 'HomepageCarousel',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fd430b0b2fa4b4635b2c7c8dd33b20fe4',
      inputs: [
        {
          name: 'OptionalHeader',
          friendlyName: 'Title',
          type: 'text',
          defaultValue: 'Sample Title',
        },
        {
          name: 'optionalSubHeader',
          friendlyName: 'Sub-header Text',
          type: 'richText',
        },
        {
          name: 'cta',
          friendlyName: 'Carousel Link',
          type: 'object',
          subFields: [
            {
              name: 'label',
              defaultValue: 'Sample cta',
              type: 'text',
            },
            {
              name: 'link',
              defaultValue: '',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
          ],
        },
        {
          name: 'items',
          helperText: 'The quantity of items should be 3, 4, 6 or more',
          type: 'list',
          subFields: [
            {
              name: 'imgSrcs',
              friendlyName: 'Image',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'altText',
              friendlyName: 'Alt Text',
              type: 'text',
              required: true,
            },
            {
              name: 'hoverImage',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: false,
            },
            {
              name: 'HoverImageAltText',
              friendlyName: 'Hover image alt text',
              type: 'text',
              required: false,
            },
            {
              name: 'name',
              type: 'text',
            },
            {
              name: 'description',
              type: 'richText',
            },
            {
              name: 'link',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
            {
              name: 'gaEvents',
              type: 'list',
              subFields: [
                {
                  name: 'dataAttributes',
                  type: 'list',
                  subFields: [
                    {
                      name: 'dataAttributeName',
                      type: 'text',
                    },
                    {
                      name: 'dataAttributeValue',
                      type: 'text',
                    },
                  ],
                },
              ],
            },
          ],
        },
        MarginField,
        AnchorField,
        {
          name: 'carouselInfo',
          type: 'object',
          advanced: true,
          subFields: [
            {
              name: 'name',
              type: 'text',
            },
            {
              name: 'label',
              type: 'text',
              enum: ['category', 'product'],
              defaultValue: 'category',
            },
          ],
        },
      ],
    },
    HomepageCarouselUpdated: {
      name: 'HomepageCarouselUpdated',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fd430b0b2fa4b4635b2c7c8dd33b20fe4',
      inputs: [
        {
          name: 'textPosition',
          type: 'string',
          enum: ['center', 'left'],
          defaultValue: 'left',
        },
        {
          name: 'optionalHeader',
          friendlyName: 'Title',
          type: 'text',
          defaultValue: 'Sample Title',
        },
        {
          name: 'optionalSubHeader',
          friendlyName: 'Sub-header Text',
          type: 'richText',
        },
        {
          name: 'cta',
          friendlyName: 'Carousel Link',
          type: 'object',
          subFields: [
            {
              name: 'label',
              defaultValue: 'Sample cta',
              type: 'text',
            },
            {
              name: 'link',
              defaultValue: '',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
          ],
        },
        {
          name: 'items',
          type: 'list',
          subFields: [
            {
              name: 'imgSrcs',
              friendlyName: 'Image',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'altText',
              friendlyName: 'Alt Text',
              type: 'text',
              required: true,
            },
            {
              name: 'hoverImage',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: false,
            },
            {
              name: 'HoverImageAltText',
              friendlyName: 'Hover image alt text',
              type: 'text',
              required: false,
            },
            {
              name: 'name',
              type: 'text',
            },
            {
              name: 'description',
              type: 'richText',
            },
            {
              name: 'link',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
          ],
        },
        {
          name: 'isFullBleed',
          type: 'boolean',
          defaultValue: false,
        },
        {
          name: 'isMobileCarousel',
          type: 'boolean',
          defaultValue: false,
        },
        MaxWidthField,
        MarginField,
        AnchorField,
        {
          name: 'carouselInfo',
          type: 'object',
          advanced: true,
          subFields: [
            {
              name: 'name',
              type: 'text',
            },
            {
              name: 'label',
              type: 'text',
              enum: ['category', 'product'],
              defaultValue: 'category',
            },
          ],
        },
      ],
    },
    ProductCarousel: {
      name: 'ProductCarousel',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fd430b0b2fa4b4635b2c7c8dd33b20fe4',
      inputs: [
        {
          name: 'textPosition',
          helperText: 'position of header text,default value is left',
          defaultValue: 'left',
          type: 'string',
          enum: ['center', 'left'],
        },
        {
          name: 'eyeBrowText',
          type: 'string',
        },
        {
          name: 'OptionalHeader',
          friendlyName: 'Title',
          type: 'text',
          defaultValue: 'Sample Title',
        },
        {
          name: 'headerTag',
          type: 'string',
          defaultValue: 'h2',
          helperText: 'Defaults to h2',
          enum: ['h2', 'h3'],
        },
        {
          name: 'optionalSubHeader',
          friendlyName: 'Sub-header Text',
          showIf: `options.get('slidesToShow') === '4' || options.get('headerTag') === 'h2'`,
          type: 'richText',
        },
        {
          name: 'cta',
          friendlyName: 'Carousel Link',
          type: 'object',
          subFields: [
            {
              name: 'label',
              defaultValue: 'Sample cta',
              type: 'text',
            },
            {
              name: 'link',
              defaultValue: '',
              regex: { ...linkRegexConfig },
              type: 'url',
            },
          ],
        },
        {
          name: 'centerText',
          friendlyName: 'Center text of slides',
          type: 'boolean',
          defaultValue: false,
        },
        {
          name: 'slidesToShow',
          helperText: '4 is put the header on the left, 6 is put the header on the top',
          type: 'string',
          defaultValue: '6',
          enum: ['4', '6'],
        },
        {
          name: 'isMobileSlider',
          helperText: 'turn on to show slider on mobile',
          type: 'boolean',
          defaultValue: true,
        },
        {
          name: 'items',
          type: 'list',
          subFields: [
            {
              name: 'imgSrcs',
              friendlyName: 'Image',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'altText',
              friendlyName: 'Image alt text',
              type: 'text',
              required: true,
            },
            {
              name: 'hoverImage',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: false,
            },
            {
              name: 'HoverImageAltText',
              friendlyName: 'Hover image alt text',
              type: 'text',
              required: false,
            },
            {
              name: 'name',
              type: 'text',
            },
            {
              name: 'description',
              type: 'richText',
            },
            {
              name: 'link',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
          ],
        },
        MaxWidthField,
        MarginField,
        AnchorField,
      ],
    },
    ProductCarouselVideo: {
      name: 'ProductCarouselVideo',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fd430b0b2fa4b4635b2c7c8dd33b20fe4',
      inputs: [
        {
          name: 'OptionalHeader',
          friendlyName: 'Title',
          type: 'text',
          defaultValue: 'Sample Title',
        },
        {
          name: 'cta',
          friendlyName: 'Carousel Link',
          type: 'object',
          subFields: [
            {
              name: 'label',
              defaultValue: 'Sample cta',
              type: 'text',
            },
            {
              name: 'link',
              regex: { ...linkRegexConfig },
              defaultValue: '',
              type: 'url',
            },
          ],
        },
        {
          name: 'centerText',
          friendlyName: 'Center text of slides',
          type: 'boolean',
          defaultValue: false,
        },
        {
          name: 'overlaySrc',
          friendlyName: 'button overlay',
          type: 'file',
          allowedFileTypes: ['svg'],
          defaultValue: 'https://image.brilliantearth.com/static/img/channel/our-movement/about/IR241/btn_play_bg.png',
          required: true,
        },
        {
          name: 'items',
          type: 'list',
          subFields: [
            {
              name: 'videoSrc',
              friendlyName: 'Video Source',
              type: 'file',
              allowedFileTypes: ['mp4', 'mov'],
            },
            {
              name: 'posterSrc',
              friendlyName: 'Poster image',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: false,
            },
            {
              name: 'title',
              type: 'text',
            },
            {
              name: 'description',
              type: 'richText',
            },
            {
              name: 'link',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
          ],
        },
        MarginField,
        AnchorField,
      ],
    },
    SearchSpringCarousel: {
      name: 'SearchSpringCarousel',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fd430b0b2fa4b4635b2c7c8dd33b20fe4',
      inputs: [
        {
          name: 'profile',
          type: 'string',
          required: true,
        },
        {
          name: 'category',
          type: 'string',
        },
        {
          name: 'title',
          type: 'text',
        },
        {
          name: 'description',
          type: 'richText',
        },
        {
          name: 'cta',
          type: 'object',
          subFields: [
            {
              name: 'label',
              type: 'text',
            },
            {
              name: 'link',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
          ],
        },
        MarginField,
        AnchorField,
      ],
    },
    StoneCarousel: {
      name: 'StoneCarousel',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fd430b0b2fa4b4635b2c7c8dd33b20fe4',
      inputs: [
        {
          name: 'title',
          type: 'object',
          subFields: [...TitleFields],
        },
        MarginField,
        AnchorField,
        {
          name: 'itemSize',
          type: 'text',
          defaultValue: 'large',
          helperText: 'Defaults to large',
          enum: ['large', 'small'],
        },
        {
          name: 'items',
          type: 'list',
          subFields: [
            {
              name: 'title',
              type: 'text',
              required: true,
            },
            {
              name: 'url',
              type: 'url',
              regex: { ...linkRegexConfig },
              required: true,
            },
            {
              name: 'img',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'alt',
              type: 'text',
              required: true,
            },
          ],
        },
      ],
    },
    DashHudsonCarousel: {
      name: 'DashHudsonCarousel',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fd430b0b2fa4b4635b2c7c8dd33b20fe4',
      inputs: [
        {
          name: 'header',
          friendlyName: 'Title',
          type: 'text',
          defaultValue: 'Get Inspired',
          required: false,
        },
        {
          name: 'cta',
          type: 'object',
          defaultValue: {
            label: 'Shop Insta',
            link: 'https://www.instagram.com/brilliantearth/',
          },
          subFields: [
            {
              name: 'label',
              type: 'text',
              defaultValue: 'Shop Insta',
            },
            {
              name: 'link',
              type: 'url',
              regex: { ...linkRegexConfig },
              defaultValue: 'https://www.instagram.com/brilliantearth/',
            },
          ],
        },
        {
          name: 'carouselID',
          type: 'text',
          defaultValue: '2980758',
          required: true,
        },
        {
          name: 'carouselName',
          type: 'text',
          defaultValue: 'likeshop-carousel',
          required: true,
        },
        {
          name: 'color',
          friendlyName: 'Background Color',
          type: 'text',
          defaultValue: 'dark-green',
          enum: ['white', 'dark-green'],
        },
        MarginField,
        AnchorField,
        {
          name: 'carouselSrc',
          type: 'text',
          advanced: true,
          defaultValue: '//cdn.dashhudson.com/web/js/carousel-embed.js',
          required: false,
        },
      ],
    },
    GatewayCarousel: {
      name: 'GatewayCarousel',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fd430b0b2fa4b4635b2c7c8dd33b20fe4',
      hideFromInsertMenu: true,
      inputs: [
        {
          name: 'title',
          type: 'object',
          subFields: [...TitleFields],
        },
        MarginField,
        AnchorField,
        {
          name: 'slidesToShowLarge',
          type: 'number',
          helperText: 'Slides to show in large screens',
          required: true,
        },
        {
          name: 'slidesToShowMedium',
          type: 'number',
          helperText:
            'Slides to show in tablets and desktop browsers. If not set is one less than the large screen value',
        },
        {
          name: 'slidesToShowSmall',
          type: 'number',
          helperText: 'Slides to show in mobile browsers. If not set is half the desktop value.',
        },
        {
          name: 'items',
          type: 'list',
          subFields: [
            {
              name: 'img',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'alt',
              type: 'text',
              defaultValue: 'slide',
            },
            {
              name: 'title',
              type: 'text',
              required: true,
            },
            {
              name: 'description',
              type: 'text',
            },
            {
              name: 'url',
              type: 'url',
              regex: { ...linkRegexConfig },
              required: true,
            },
            {
              name: 'newTab',
              type: 'boolean',
              defaultValue: false,
            },
          ],
        },
        {
          name: 'optionalLink',
          type: 'object',
          subFields: [
            {
              name: 'text',
              type: 'text',
            },
            {
              name: 'url',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
            {
              name: 'newTab',
              type: 'boolean',
              defaultValue: false,
            },
          ],
        },
        {
          name: 'type',
          type: 'text',
          enum: ['indented', 'finite', 'infinite'],
          defaultValue: 'indented',
          helperText: 'Defaults to the left "indented" finite carosuel',
        },
      ],
    },
    Faqs: {
      name: 'Faqs',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F442be2924c314a0a99a7b8cef608636f',
      inputs: [
        {
          name: 'optionalLargeHeader',
          type: 'object',
          subFields: [...TitleFields],
        },
        {
          name: 'schema',
          type: 'boolean',
          helperText: 'Turn on to add a schema markup',
          defaultValue: false,
        },
        MarginField,
        AnchorField,
        {
          name: 'items',
          type: 'list',
          subFields: [
            {
              name: 'question',
              type: 'object',
              subFields: [...TitleFields],
            },
            {
              name: 'answer',
              type: 'richText',
            },
          ],
        },
      ],
    },
    FaqParagraph: {
      name: 'FaqParagraph',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F442be2924c314a0a99a7b8cef608636f',
      inputs: [
        {
          name: 'title',
          type: 'text',
          defaultValue: 'Large Header ',
        },
        {
          name: 'description',
          type: 'richText',
          defaultValue:
            'Example text here this line is optional. Paragraph style. Example text here this line is optional.',
        },
        MarginField,
        AnchorField,
      ],
    },
    BoxWidget: {
      name: 'Box Widget',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F22ebd2065c884be7884772c087422157',
      inputs: [
        {
          name: 'optionalLargeHeader',
          type: 'object',
          subFields: [...TitleFields],
        },
        {
          name: 'title',
          helperText: 'Title description for screen readers',
          type: 'string',
        },
      ],
    },
    JazzHRTable: {
      name: 'Jazz HR Table',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fc415de7acd63458abdcf6da15ed2f678',
      hideFromInsertMenu: true,
      inputs: [
        {
          name: 'optionalLargeHeader',
          type: 'text',
          helperText: 'Type the desired title.',
        },
        {
          name: 'description',
          type: 'richText',
          helperText: 'Type the desired description.',
        },
        {
          name: 'title',
          helperText: 'Title description for screen readers',
          type: 'string',
        },
        {
          name: 'ctas',
          friendlyName: 'Actions',
          type: 'list',
          subFields: [
            {
              name: 'isGeneralLinkTileCtaDropdown',
              type: 'boolean',
              defaultValue: false,
              friendlyName: 'Is this a dropdown button?',
              helperText:
                'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
            },
            {
              name: 'label',
              type: 'text',
            },
            {
              name: 'href',
              friendlyName: 'Link',
              type: 'url',
              regex: { ...linkRegexConfig },
              showIf: `!options.get('isGeneralLinkTileCtaDropdown')`,
            },
            {
              name: 'type',
              friendlyName: 'Variant',
              type: 'string',
              defaultValue: 'secondary',
              enum: ['primary', 'secondary'],
              required: false,
            },
            {
              name: 'color',
              type: 'string',
              defaultValue: 'dark',
              enum: ['light', 'dark'],
              required: false,
            },
            {
              name: 'actions',
              type: 'list',
              showIf: `options.get('isGeneralLinkTileCtaDropdown')`,
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
                {
                  name: 'type',
                  friendlyName: 'Variant',
                  type: 'string',
                  defaultValue: 'secondary',
                  enum: ['primary', 'secondary'],
                  required: false,
                },
                {
                  name: 'color',
                  type: 'string',
                  defaultValue: 'dark',
                  enum: ['light', 'dark'],
                  required: false,
                },
              ],
            },
          ],
        },
        MarginField,
        AnchorField,
      ],
    },
    StoreLocator: {
      name: 'StoreLocator',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F64dc7026f1c94142b45dccbe5e1afe17',
      inputs: [
        {
          name: 'title',
          type: 'object',
          required: true,
          subFields: [
            {
              name: 'content',
              helperText: 'Type the desired title.',
              type: 'text',
              required: true,
            },
            {
              name: 'titleHTMLTag',
              type: 'string',
              defaultValue: 'h1',
              helperText: 'Defaults to h1',
              enum: ['h1', 'h2'],
              required: true,
            },
          ],
        },
        {
          name: 'description',
          type: 'richText',
          helperText: 'Optional field.',
        },
        MarginField,
      ],
    },
    StoreInfoBlock: {
      name: 'StoreInfoBlock',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F64dc7026f1c94142b45dccbe5e1afe17',
      inputs: [
        {
          name: 'store',
          type: 'reference',
          model: 'showrooms',
          required: true,
        },
        MarginField,
      ],
    },
    TableOfContents: {
      name: 'TableOfContents',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F64dc7026f1c94142b45dccbe5e1afe17',
      inputs: [
        {
          name: 'title',
          type: 'string',
        },
        {
          name: 'menuItems',
          friendlyName: 'Anchor Links',
          type: 'list',
          subFields: [
            {
              name: 'text',
              type: 'text',
            },
            {
              name: 'anchor',
              type: 'url',
            },
          ],
        },
        MarginField,
      ],
    },
    LastUpdated: {
      name: 'LastUpdated',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F64dc7026f1c94142b45dccbe5e1afe17',
      // Date value lives on the page model field `lastUpdatedAt` (not on this component).
      inputs: [],
      defaultStyles: {
        marginTop: '0px',
      },
    },
    XYChart: {
      name: 'XYChart',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F64dc7026f1c94142b45dccbe5e1afe17',
      inputs: [
        {
          name: 'title',
          friendlyName: 'Header',
          type: 'object',
          subFields: [...TitleFields],
        },
        {
          name: 'description',
          friendlyName: 'Subheader',
          type: 'richText',
          helperText: 'Optional field.',
        },
        {
          name: 'columnHeaders',
          friendlyName: 'Column Headers',
          type: 'list',
          subFields: [
            {
              name: 'cell',
              friendlyName: 'Cell',
              type: 'object',
              subFields: [
                {
                  name: 'content',
                  type: 'richText',
                },
                {
                  name: 'horizontalAlignment',
                  friendlyName: 'Text Alignment',
                  type: 'text',
                  enum: ['left', 'center'],
                  defaultValue: 'left',
                  helperText: 'Defaults to center',
                },
                {
                  name: 'cellColor',
                  type: 'text',
                  enum: ['White', 'Grey 50', 'Grey 100'],
                },
                {
                  name: 'columnWidth',
                  type: 'number',
                },
                {
                  name: 'minColumnWidth',
                  type: 'number',
                  helperText: 'Minimum column width for mobile devices',
                },
              ],
            },
          ],
        },
        {
          name: 'rows',
          type: 'list',
          subFields: [
            {
              name: 'cells',
              friendlyName: 'Cells',
              type: 'list',
              subFields: [
                {
                  name: 'cell',
                  friendlyName: 'Cell',
                  type: 'object',
                  subFields: [
                    {
                      name: 'content',
                      type: 'richText',
                    },
                    {
                      name: 'fontWeight',
                      type: 'text',
                      enum: ['Avenir Book', 'Avenir Medium', 'Avenir Heavy'],
                    },
                    {
                      name: 'cellColor',
                      type: 'text',
                      enum: ['White', 'Grey 50', 'Grey 100'],
                    },
                    {
                      name: 'horizontalAlignment',
                      type: 'text',
                      enum: ['left', 'center'],
                      defaultValue: 'left',
                    },
                    {
                      name: 'verticalAlignment',
                      type: 'text',
                      enum: ['top', 'center'],
                      defaultValue: 'left',
                    },
                  ],
                },
              ],
            },
          ],
        },
        MarginField,
      ],
    },
    MainTag: {
      name: 'MainTag',
      canHaveChildren: true,
      noWrap: false,
      defaultChildren: [{ '@type': '@builder.io/sdk:Element' }],
      defaultStyles: {
        paddingTop: '25px',
        paddingBottom: '25px',
      },
    },
    ArticleTag: {
      name: 'ArticleTag',
      canHaveChildren: true,
      noWrap: false,
      defaultChildren: [{ '@type': '@builder.io/sdk:Element' }],
      defaultStyles: {
        paddingTop: '25px',
        paddingBottom: '25px',
      },
    },
    AsideTag: {
      name: 'AsideTag',
      canHaveChildren: true,
      noWrap: false,
      defaultChildren: [{ '@type': '@builder.io/sdk:Element' }],
      defaultStyles: {
        paddingTop: '25px',
        paddingBottom: '25px',
      },
    },
    DoubleImageBanner: {
      name: 'DoubleImageBanner',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F64dc7026f1c94142b45dccbe5e1afe17',
      inputs: [
        {
          name: 'image1',
          type: 'object',
          subFields: [
            {
              name: 'src',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'mobileSrc',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'altText',
              type: 'string',
              defaultValue: 'Set the Alt value for the image here.',
              required: true,
            },
            { name: 'href', regex: { ...linkRegexConfig }, type: 'string' },
            { name: 'title', type: 'string', required: true },
            { name: 'description', type: 'richText' },
            {
              name: 'copyColor',
              type: 'string',
              defaultValue: 'white',
              enum: ['white', 'black'],
            },
            ActionsField,
          ],
        },
        {
          name: 'image2',
          type: 'object',
          subFields: [
            {
              name: 'src',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'mobileSrc',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'altText',
              type: 'string',
              defaultValue: 'Set the Alt value for the image here.',
              required: true,
            },
            { name: 'href', regex: { ...linkRegexConfig }, type: 'string' },
            { name: 'title', type: 'string' },
            { name: 'description', type: 'richText' },
            {
              name: 'copyColor',
              type: 'string',
              defaultValue: 'white',
              enum: ['white', 'black'],
            },
            ActionsField,
          ],
        },
        {
          name: 'textPosition',
          type: 'string',
          defaultValue: 'perImages',
          enum: ['acrossImages', 'perImages'],
        },
        {
          name: 'alignmentOptions',
          type: 'string',
          defaultValue: 'bottom',
          enum: ['center', 'bottom'],
        },
        AnchorField,
        OutlineMarginField,
        MarginField,
      ],
    },
    ShopByShape: {
      name: 'ShopByShape',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fd430b0b2fa4b4635b2c7c8dd33b20fe4',
      inputs: [
        {
          name: 'header',
          type: 'object',
          subFields: [...TitleFields],
        },
        {
          name: 'textPosition',
          type: 'string',
          defaultValue: 'center',
          enum: ['left', 'center', 'right'],
        },
        {
          name: 'ringItems',
          type: 'list',
          subFields: [
            {
              name: 'img',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'alt',
              type: 'string',
              required: true,
            },
            {
              name: 'link',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
            {
              name: 'title',
              type: 'string',
              helperText: 'Same as the title of the corresponding diamondItem',
              required: true,
            },
          ],
        },
        {
          name: 'diamondItems',
          type: 'list',
          subFields: [
            {
              name: 'img',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'alt',
              type: 'string',
              required: true,
            },
            {
              name: 'url',
              type: 'url',
              regex: { ...linkRegexConfig },
              required: true,
            },
            {
              name: 'title',
              helperText: 'Must match Ring Title name for hover effect',
              type: 'string',
              required: true,
            },
          ],
        },
        MarginField,
        OutlineMarginField,
        AnchorField,
      ],
    },
    ThreeImageTile: {
      name: 'ThreeImageTile',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F64dc7026f1c94142b45dccbe5e1afe17',
      inputs: [
        { name: 'header', type: 'string' },
        { name: 'headerTag', type: 'string', defaultValue: 'h2', enum: ['h1', 'h2'] },
        { name: 'description', type: 'richText' },
        { name: 'headerPosition', type: 'string', enum: ['center', 'left'], defaultValue: 'left' },
        {
          name: 'image1',
          type: 'object',
          subFields: [
            {
              name: 'src',
              type: 'file',
              friendlyName: 'Image',
              allowedFileTypes: ['jpeg', 'jpg', 'gif'],
              required: true,
            },
            {
              name: 'altText',
              type: 'string',
              defaultValue: 'Set the Alt value for the image here.',
              required: true,
            },
            {
              name: 'href',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
            {
              name: 'textAndButton',
              type: 'object',
              subFields: [
                { name: 'title', type: 'text' },
                {
                  name: 'titleTag',
                  type: 'string',
                  defaultValue: 'h3',
                  enum: ['h2', 'h3', 'h4'],
                },
                {
                  name: 'description',
                  type: 'richText',
                  defaultValue:
                    'Example text here this line is optional. Paragraph style. Example text here this line is optional.',
                },
                {
                  name: 'copyColor',
                  type: 'string',
                  defaultValue: 'white',
                  enum: ['white', 'black'],
                  required: false,
                },
                ActionsField,
              ],
            },
          ],
        },
        {
          name: 'image2',
          type: 'object',
          subFields: [
            {
              name: 'src',
              type: 'file',
              friendlyName: 'Image',
              allowedFileTypes: ['jpeg', 'jpg', 'gif'],
              required: true,
            },
            {
              name: 'altText',
              type: 'string',
              defaultValue: 'Set the Alt value for the image here.',
              required: true,
            },
            {
              name: 'href',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
            {
              name: 'textAndButton',
              type: 'object',
              subFields: [
                { name: 'title', type: 'text' },
                {
                  name: 'titleTag',
                  type: 'string',
                  defaultValue: 'h3',
                  enum: ['h2', 'h3', 'h4'],
                },
                {
                  name: 'description',
                  type: 'richText',
                  defaultValue:
                    'Example text here this line is optional. Paragraph style. Example text here this line is optional.',
                },
                {
                  name: 'copyColor',
                  type: 'string',
                  defaultValue: 'white',
                  enum: ['white', 'black'],
                  required: false,
                },
                ActionsField,
              ],
            },
          ],
        },
        {
          name: 'image3',
          type: 'object',
          subFields: [
            {
              name: 'src',
              type: 'file',
              friendlyName: 'Image',
              allowedFileTypes: ['jpeg', 'jpg', 'gif'],
              required: true,
            },
            {
              name: 'altText',
              type: 'string',
              defaultValue: 'Set the Alt value for the image here.',
              required: true,
            },
            {
              name: 'href',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
            {
              name: 'textAndButton',
              type: 'object',
              subFields: [
                { name: 'title', type: 'text' },
                {
                  name: 'titleTag',
                  type: 'string',
                  defaultValue: 'h3',
                  enum: ['h2', 'h3', 'h4'],
                },
                {
                  name: 'description',
                  type: 'richText',
                  defaultValue:
                    'Example text here this line is optional. Paragraph style. Example text here this line is optional.',
                },
                {
                  name: 'copyColor',
                  type: 'string',
                  defaultValue: 'white',
                  enum: ['white', 'black'],
                  required: false,
                },
                ActionsField,
              ],
            },
          ],
        },
        {
          name: 'isTextUnderImage',
          type: 'boolean',
          defaultValue: false,
        },
        {
          name: 'textPosition',
          type: 'string',
          defaultValue: 'end',
          enum: ['top', 'center', 'end'],
          showIf: `!options.get('isTextUnderImage')`,
        },
        AnchorField,
        OutlineMarginField,
        MarginField,
      ],
    },
    NavarTrackInfo: {
      name: 'NavarTrackInfo',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fd430b0b2fa4b4635b2c7c8dd33b20fe4',
      inputs: [
        { name: 'header', type: 'string' },
        { name: 'headerTag', type: 'string', defaultValue: 'h2', enum: ['h1', 'h2'] },
        {
          name: 'previewOrderNumber',
          friendlyName: 'Preview Order Number',
          type: 'string',
          defaultValue: '20261693395',
          helperText:
            'Builder preview only. Used with Preview Email to load a live NS order when the URL has no o/t. Updates apply in the preview without a full page refresh. Leave email empty to use the built-in sample token for the default order.',
        },
        {
          name: 'previewEmail',
          friendlyName: 'Preview Email',
          type: 'string',
          defaultValue: '',
          helperText:
            'Builder preview only. Customer email for the Preview Order Number (NS email lookup). Required when previewing an order other than the default sample.',
        },
        {
          name: 'orderStatus',
          type: 'object',
          hideFromUI: true,
          helperText: 'Injected server-side from NetSuite for /orderstatus/?o=&t=',
        },
        {
          name: 'orderStatusError',
          type: 'string',
          hideFromUI: true,
          helperText: 'Injected server-side when NetSuite lookup fails',
        },
        {
          name: 'categoryGroups',
          type: 'list',
          subFields: [
            { name: 'category', type: 'string' },
            {
              name: 'heroBackground',
              helperText: 'In case of a video background, the images are used as Video thumbnails',
              type: 'object',
              subFields: [
                {
                  name: 'desktop',
                  type: 'file',
                  defaultValue: '/bee2-ui/img/homepage/BE_HP_HeroBanner_Desktop.jpg',
                  allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
                  required: true,
                },
                {
                  name: 'mobile',
                  type: 'file',
                  defaultValue: '/bee2-ui/img/homepage/BE_HP_HeroBanner_Mobile.jpg',
                  allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
                  required: true,
                },
              ],
            },
            {
              name: 'heroImgAlt',
              type: 'string',
              defaultValue: 'Set the Alt value for the image here.',
              required: true,
            },
            {
              name: 'heroIsWistia',
              type: 'boolean',
              defaultValue: false,
              friendlyName: 'Wistia video link?',
            },
            {
              name: 'heroDesktopVideoUri',
              friendlyName: 'Desktop Wistia Video URL',
              type: 'url',
              required: false,
              showIf: `options.get('heroIsWistia')`,
            },
            {
              name: 'heroMobileVideoUri',
              friendlyName: 'Mobile Wistia Video URL ',
              type: 'url',
              required: false,
              showIf: `options.get('heroIsWistia')`,
            },
            {
              name: 'heroFileVideoSrc',
              friendlyName: 'Video Source',
              type: 'file',
              allowedFileTypes: ['mp4', 'mov'],
              required: false,
              showIf: `!options.get('heroIsWistia')`,
            },
            {
              name: 'heroFileMobileVideoSrc',
              friendlyName: 'Mobile Video Source',
              type: 'file',
              allowedFileTypes: ['mp4', 'mov'],
              required: false,
              showIf: `!options.get('heroIsWistia')`,
            },
            {
              name: 'heroAutoPlay',
              type: 'boolean',
              defaultValue: true,
              showIf: `!options.get('heroIsWistia')`,
            },
            {
              name: 'heroLoop',
              type: 'boolean',
              defaultValue: true,
              showIf: `!options.get('heroIsWistia')`,
            },
            {
              name: 'heroControls',
              type: 'boolean',
              defaultValue: true,
              showIf: `!options.get('heroIsWistia')`,
            },
            {
              name: 'heroHasSound',
              type: 'boolean',
              defaultValue: true,
            },
            { name: 'heroTitle', friendlyName: 'Desktop Title', type: 'text' },
            { name: 'heroMobileTitle', friendlyName: 'Mobile Title', type: 'text' },
            { name: 'heroDescription', type: 'richText' },
            {
              name: 'heroActions',
              type: 'list',
              // onChange: options => {
              //   if (options.get['actions'].length > 2) {
              //     options.set('actions', options.get('actions').slice(0, 2));
              //   }
              // },
              subFields: [
                {
                  name: 'isDropdownButton',
                  type: 'boolean',
                  defaultValue: false,
                  friendlyName: 'Is this a dropdown button?',
                  helperText:
                    'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
                },
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'variant',
                  friendlyName: 'Desktop variant',
                  type: 'text',
                  enum: ['primary', 'secondary'],
                  defaultValue: 'primary',
                },
                {
                  name: 'color',
                  friendlyName: 'Desktop color',
                  type: 'text',
                  enum: ['light', 'dark'],
                  defaultValue: 'dark',
                },
                {
                  name: 'mobileVariant',
                  type: 'text',
                  enum: ['primary', 'secondary'],
                  defaultValue: 'primary',
                },
                {
                  name: 'mobileColor',
                  type: 'text',
                  enum: ['light', 'dark'],
                  defaultValue: 'dark',
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                  showIf: `!options.get('isDropdownButton')`,
                },
                {
                  name: 'actions',
                  type: 'list',
                  subFields: [
                    {
                      name: 'label',
                      type: 'text',
                    },
                    {
                      name: 'href',
                      type: 'url',
                      regex: { ...linkRegexConfig },
                    },
                  ],
                  showIf: `options.get('isDropdownButton')`,
                },
              ],
            },
            {
              name: 'heroCopyColor',
              friendlyName: 'Desktop Copy Color',
              type: 'string',
              defaultValue: 'black',
              enum: ['white', 'black'],
              required: false,
            },
            {
              name: 'heroSectionLink',
              type: 'url',
              regex: { ...linkRegexConfig },
              required: false,
            },
            {
              name: 'heroDesktopCopyPosition',
              type: 'text',
              defaultValue: 'right',
              enum: ['left', 'right', 'center', 'center/center'],
            },
            {
              name: 'image1',
              type: 'object',
              subFields: [
                {
                  name: 'src',
                  type: 'file',
                  friendlyName: 'Image',
                  allowedFileTypes: ['jpeg', 'jpg', 'gif'],
                  required: true,
                },
                {
                  name: 'altText',
                  type: 'string',
                  defaultValue: 'Set the Alt value for the image here.',
                  required: true,
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
                {
                  name: 'textAndButton',
                  type: 'object',
                  subFields: [
                    { name: 'title', type: 'text' },
                    {
                      name: 'titleTag',
                      type: 'string',
                      defaultValue: 'h3',
                      enum: ['h2', 'h3', 'h4'],
                    },
                    {
                      name: 'description',
                      type: 'richText',
                      defaultValue:
                        'Example text here this line is optional. Paragraph style. Example text here this line is optional.',
                    },
                    {
                      name: 'copyColor',
                      type: 'string',
                      defaultValue: 'white',
                      enum: ['white', 'black'],
                      required: false,
                    },
                    ActionsField,
                  ],
                },
              ],
            },
            {
              name: 'image2',
              type: 'object',
              subFields: [
                {
                  name: 'src',
                  type: 'file',
                  friendlyName: 'Image',
                  allowedFileTypes: ['jpeg', 'jpg', 'gif'],
                  required: true,
                },
                {
                  name: 'altText',
                  type: 'string',
                  defaultValue: 'Set the Alt value for the image here.',
                  required: true,
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
                {
                  name: 'textAndButton',
                  type: 'object',
                  subFields: [
                    { name: 'title', type: 'text' },
                    {
                      name: 'titleTag',
                      type: 'string',
                      defaultValue: 'h3',
                      enum: ['h2', 'h3', 'h4'],
                    },
                    {
                      name: 'description',
                      type: 'richText',
                      defaultValue:
                        'Example text here this line is optional. Paragraph style. Example text here this line is optional.',
                    },
                    {
                      name: 'copyColor',
                      type: 'string',
                      defaultValue: 'white',
                      enum: ['white', 'black'],
                      required: false,
                    },
                    ActionsField,
                  ],
                },
              ],
            },
            {
              name: 'image3',
              type: 'object',
              subFields: [
                {
                  name: 'src',
                  type: 'file',
                  friendlyName: 'Image',
                  allowedFileTypes: ['jpeg', 'jpg', 'gif'],
                  required: true,
                },
                {
                  name: 'altText',
                  type: 'string',
                  defaultValue: 'Set the Alt value for the image here.',
                  required: true,
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
                {
                  name: 'textAndButton',
                  type: 'object',
                  subFields: [
                    { name: 'title', type: 'text' },
                    {
                      name: 'titleTag',
                      type: 'string',
                      defaultValue: 'h3',
                      enum: ['h2', 'h3', 'h4'],
                    },
                    {
                      name: 'description',
                      type: 'richText',
                      defaultValue:
                        'Example text here this line is optional. Paragraph style. Example text here this line is optional.',
                    },
                    {
                      name: 'copyColor',
                      type: 'string',
                      defaultValue: 'white',
                      enum: ['white', 'black'],
                      required: false,
                    },
                    ActionsField,
                  ],
                },
              ],
            },
            {
              name: 'isTextUnderImage',
              type: 'boolean',
              defaultValue: false,
            },
            {
              name: 'textPosition',
              type: 'string',
              defaultValue: 'end',
              enum: ['top', 'center', 'end'],
              showIf: `!options.get('isTextUnderImage')`,
            },
          ],
        },
        MarginField,
      ],
    },
    MultiImage: {
      name: 'MultiImage',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F64dc7026f1c94142b45dccbe5e1afe17',
      inputs: [
        {
          name: 'hasGutters',
          defaultValue: false,
          type: 'boolean',
        },
        {
          name: 'images',
          type: 'list',
          subFields: [
            {
              name: 'src',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'alt',
              type: 'string',
              required: true,
            },
            {
              name: 'href',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
            { name: 'title', type: 'text' },
            {
              name: 'subHeader',
              type: 'richText',
            },
            {
              name: 'copyColor',
              type: 'string',
              defaultValue: 'white',
              enum: ['white', 'black'],
              required: false,
            },
            {
              name: 'position',
              helperText: 'the position of the header and cta,default to end',
              type: 'string',
              defaultValue: 'end',
              enum: ['top', 'center', 'end'],
            },
            ActionsField,
          ],
          required: true,
        },
        AnchorField,
        OutlineMarginField,
        MarginField,
      ],
    },
    Storytelling: {
      name: 'Storytelling',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F64dc7026f1c94142b45dccbe5e1afe17',
      inputs: [
        {
          name: 'backgroundColor',
          helperText: 'Ex. #ffffff',
          type: 'string',
        },
        {
          name: 'eyeBrowText',
          helperText: 'Right side EyeBrow Text',
          showIf: `options.get('types') && options.get('types') !== 'Guided'`,
          type: 'string',
        },
        {
          name: 'header',
          helperText: 'Right Side Header',
          showIf: `options.get('types')`,
          type: 'string',
        },
        {
          name: 'headerTag',
          helperText: 'Right Side Header Tag, default Value is h3',
          showIf: `options.get('types')`,
          type: 'string',
          defaultValue: 'h3',
          enum: ['h2', 'h3'],
        },
        {
          name: 'description',
          helperText: 'Right Side Subcopy',
          showIf: `options.get('types') && options.get('types') === 'Feature'`,
          type: 'richText',
        },
        {
          name: 'textAlignment',
          helperText: 'Right Side Text Alignment, default Value is center',
          showIf: `options.get('types')`,
          type: 'string',
          defaultValue: 'center',
          enum: ['left', 'center'],
        },
        {
          name: 'textColor',
          helperText: 'Right Side Text Color',
          showIf: `options.get('types')`,
          type: 'string',
          defaultValue: 'white',
          enum: ['white', 'black'],
          required: false,
        },
        { ...ActionsField, helperText: 'Right Side CTA', showIf: `options.get('types')` },
        {
          name: 'types',
          onChange: options => {
            if (options.get('types') === 'Feature') {
              options.set('images', options.get('images').slice(0, 2));
            }
            if (options.get('types') === 'Guided') {
              options.set('images', options.get('images').slice(0, 4));
            }
          },
          type: 'string',
          enum: ['Carousel', 'Feature', 'Guided'],
        },
        {
          name: 'bigImage',
          showIf: `options.get('types')`,
          type: 'object',
          subFields: [
            {
              name: 'src',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'alt',
              type: 'string',
              required: true,
            },
            {
              name: 'href',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
            { name: 'title', helperText: 'Big Image Header', type: 'text' },
            {
              name: 'titleTag',
              helperText: 'Big Image Header Tag, default Value is h3',
              type: 'string',
              defaultValue: 'h3',
              enum: ['h2', 'h3', 'h4'],
            },
            {
              name: 'eyeText',
              helperText: 'Big Image Eyebrow Text',
              type: 'string',
            },
            {
              name: 'imageTextColor',
              helperText: 'Big Image Text Color, default Value is white',
              type: 'string',
              defaultValue: 'white',
              enum: ['white', 'black'],
              required: false,
            },
            {
              name: 'horizontalPosition',
              helperText: 'Big Image Text Box Horizontal Position, default Value is center',
              type: 'string',
              defaultValue: 'center',
              enum: ['left', 'center', 'right'],
            },
            {
              name: 'verticalPosition',
              helperText: 'Big Image Text Box Vertical Position, default Value is center',
              type: 'string',
              defaultValue: 'center',
              enum: ['top', 'center', 'bottom'],
            },
            { ...ActionsField, name: 'imageActions' },
          ],
          required: true,
        },
        {
          name: 'isAddBigImageWidth',
          helperText: 'turn on set image width is half of component',
          defaultValue: false,
          type: 'boolean',
          showIf: `options.get('types')`,
        },
        {
          name: 'isBigImageRight',
          helperText: 'turn on to show the big image on the right',
          defaultValue: false,
          type: 'boolean',
          showIf: `options.get('types') && options.get('types') !== 'Carousel'`,
        },
        {
          name: 'smallImageTextColor',
          friendlyName: 'Text color on small image',
          helperText: 'Ex. #ffffff',
          type: 'string',
          showIf: `options.get('types')`,
        },
        {
          name: 'textPosition',
          friendlyName: 'Text position under small images',
          helperText: 'text position under small image, default Value is center',
          showIf: `options.get('types')`,
          type: 'string',
          defaultValue: 'center',
          enum: ['left', 'center'],
        },
        {
          name: 'isTwoImageOneLine',
          helperText: 'turn on to show two image on one line(only on mobile)',
          showIf: `options.get('types') && options.get('types') === 'Feature'`,
          defaultValue: false,
          type: 'boolean',
        },
        {
          name: 'images',
          type: 'list',
          showIf: `options.get('types')`,
          subFields: [
            {
              name: 'src',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'alt',
              type: 'string',
              required: true,
            },
            {
              name: 'href',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
            {
              name: 'productName',
              type: 'string',
            },
            {
              name: 'price',
              type: 'string',
            },
          ],
          required: true,
        },
        AnchorField,
        OutlineMarginField,
        MarginField,
      ],
    },
  },
};

export default commonComponents;
